import { mapActions, mapState } from 'vuex'
import flowModule from '@/modules/Flow/store'
import {
  FLOW_MODULE_NAME,
  FLOW_SET_NAME,
  FLOW_SET_STATE_NAME,
  FLOW_SET_STATE_TRANSITIONS,
  FLOW_SET_STATE_VIEW_CONTENT,
  FLOW_SET_STATE_VIEW_OPTIONS,
  FLOW_SET_COMPANY_ICON
} from '@/modules/Flow/store/types'
import { USER_MODULE_NAME } from '@/modules/User/store/types'
import {
  WARRANTY_MODULE_NAME,
  FNOL_MODULE_NAME,
  UPDATE_LOADER
} from '@/store/types'
import {
  apiGetFlowData,
  apiGetTransitionToNextState
} from '@/modules/Flow/services/flow'
import { flowStates } from '@/modules/Flow/config/flow'
import setTimeStamp from '@/utils/timeStamp.js'
import { apiPostTimeStamp } from '@/modules/User/services/timeStamp'
import { FLOW_NAMES, FLOW_TRANSITIONS, OTHERS } from '@/modules/Flow/constants'

const isValidState = (stateName) => {
  const selectedState = flowStates.filter(
    (state) => state.name === stateName
  )[0]
  return selectedState
}

export default {
  data: function () {
    return {
      stateIsLoading: false
    }
  },

  computed: {
    ...mapState(WARRANTY_MODULE_NAME, {
      stateWarrantySinisterId: 'sinisterId',
      stateWarrantyId: 'warrantyId',
      stateWarrantyPropertyType: 'propertyType'
    }),

    ...mapState(USER_MODULE_NAME, {
      stateUserId: 'userId'
    }),

    ...mapState(FNOL_MODULE_NAME, {
      stateIdFlux: 'idFlux'
    }),

    ...mapState(FLOW_MODULE_NAME, {
      stateFlowName: 'flowName',
      stateFlowCompanyIcon: 'flowCompanyIcon',
      stateFlowState: 'flowState',
      stateFlowTransitions: 'flowTransitions',
      stateFlowViewContent: 'flowViewContent',
      stateFlowViewOptions: 'flowViewOptions'
    }),

    getSinisterId() {
      return (
        this.stateWarrantySinisterId ||
        this.$route.query.sinisterId ||
        sessionStorage.getItem('sinId')
      )
    },

    getUser() {
      return this.stateUserId || this.$route.params.user
    },

    getWarrantyId() {
      return (
        this.stateWarrantyId ||
        this.$route.params.warrantyId ||
        sessionStorage.getItem('formId')
      )
    },

    getFnolId() {
      return this.stateIdFlux
    },

    getPropertyType() {
      return this.stateWarrantyPropertyType || this.$route.params.propertyType
    },

    getDamageType() {
      return this.$route.params.damageType
    },

    getDamagedItem() {
      return this.$route.params.damagedItem
    },

    getProductId() {
      return this.$route.params.productId
    },

    getDamageName() {
      return this.$route.params.name
    },

    getFlowName() {
      return this.stateFlowName || null
    },

    getFlowState() {
      return this.stateFlowState || null
    },

    getFlowTransitions() {
      return this.stateFlowTransitions || null
    },

    getFlowViewContent() {
      return this.stateFlowViewContent || null
    },

    getFlowViewOptions() {
      return this.stateFlowViewOptions || null
    },

    getFlowStateViewName() {
      const funtionalityState = flowStates.filter(
        (functionality) => functionality.name === this.getFlowState
      )[0]
      return funtionalityState ? funtionalityState.entryPointViewName : null
    },

    isE2EFlow() {
      return this.getFlowName === FLOW_NAMES.E2E
    },

    hasMobilierTransition() {
      return this.getFlowTransitions.includes(FLOW_TRANSITIONS.SHOW_MOBILIER)
    },

    // STATE OPTIONS
    addDamagesHasImages() {
      if (this.getDamageType !== OTHERS) {
        return this.getFlowViewOptions
          ? this.getFlowViewOptions.imagesRequired
          : false
      }
    },

    getPhotoclaimForms() {
      return this.getFlowViewOptions
        ? this.getFlowViewOptions.photoclaimForms
        : null
    },

    getFlowCompanyIcon() {
      return this.stateFlowCompanyIcon
    }
  },
  methods: {
    ...mapActions(FLOW_MODULE_NAME, {
      actionFlowSetName: FLOW_SET_NAME,
      actionFlowSetCompanyIcon: FLOW_SET_COMPANY_ICON,
      actionFlowSetState: FLOW_SET_STATE_NAME,
      actionFlowSetTransitions: FLOW_SET_STATE_TRANSITIONS,
      actionFlowSetViewContent: FLOW_SET_STATE_VIEW_CONTENT,
      actionFlowSetViewOptions: FLOW_SET_STATE_VIEW_OPTIONS
    }),

    setFlowName(flowName) {
      this.actionFlowSetName(flowName)
    },

    setFlowCompanyIcon(iconUrl) {
      this.actionFlowSetCompanyIcon(iconUrl)
    },

    setFlowState(stateName, stateTransitions) {
      if (!stateName) return

      if (isValidState(stateName)) {
        this.actionFlowSetState(stateName)
        this.actionFlowSetTransitions(stateTransitions)
      } else {
        console.error(`State error: ${stateName} is not a valid state`)
        this.goToErrorView()
      }
    },

    async getStateFlowData() {
      try {
        const stateData = await apiGetFlowData(sessionStorage.getItem('sinId'))
        const { options } = stateData
        this.setViewOptions(options)
        return stateData
      } catch (e) {
        if (this.$route.name !== 'error') {
          this.$router.replace({ name: 'error' })
        } // or redirect to youNeedMAValueLinkView
        throw new Error(e)
      }
    },

    async getTransitionToNextState(transitionEvent, eventOptions = null) {
      try {
        const nextStateData = await apiGetTransitionToNextState(
          sessionStorage.getItem('sinId'),
          transitionEvent,
          eventOptions
        )
        return nextStateData
      } catch (e) {
        if (this.$route.name !== 'error') {
          this.$router.replace({ name: 'error' })
        } // or redirect to youNeedMAValueLinkView
        throw new Error(e)
      }
    },

    goToView(viewName, viewPathParams) {
      this.$router
        .push({
          name: viewName,
          params: {
            user: this.getUser,
            sinisterId: this.getSinisterId,
            warrantyId: this.getWarrantyId,
            ...viewPathParams
          }
        })
        .catch((e) => {})
    },

    async goToNextState(event = null, eventOptions) {
      const appViewTransitionDuration = 600

      if (this.stateIsLoading) return
      this.handleLoadingState(true)

      const nextTransition = event || this.getFlowTransitions[0] || null
      const nextStateData = await this.getTransitionToNextState(
        nextTransition,
        eventOptions
      )

      const { state, transitions, options } = nextStateData
      this.goToStateView(state, transitions)
      if (options.content) this.setViewContent(options.content)
      this.setViewOptions(options)

      setTimeout(() => {
        this.handleLoadingState(false)
      }, appViewTransitionDuration)
    },

    handleLoadingState(stateIsLoading) {
      this.stateIsLoading = stateIsLoading
      this.$store.commit(UPDATE_LOADER, stateIsLoading)
    },

    goToStateView(stateName, stateTransitions) {
      this.setFlowState(stateName, stateTransitions)
      this.goToView(this.getFlowStateViewName)
    },

    goToErrorView() {
      if (this.$route.name !== 'error') {
        this.$router.replace({ name: 'error' })
      }
    },

    goBack() {
      const path = this.$router.navigationHistory.pop() || 'damages-view'
      const propertyType = this.getPropertyType
      this.goToView(path, { propertyType })
      this.$router.navigationHistory.pop()
    },

    trackUserFlowTimeStamp(message, sinisterId) {
      const timeStamp = setTimeStamp()

      const timeStampParams = {
        description: message,
        sinister_id: sinisterId,
        start: timeStamp,
        end: timeStamp
      }

      apiPostTimeStamp(timeStampParams).catch((err) => {
        const errs = [400, 500, 409, 404]
        const errCode =
          errs.find((errCode) => err.response.status === errCode) || 0
        this.throwNotification(
          this.$t(`notifications.user.error.${errCode}.title`),
          this.$t(`notifications.user.error.${errCode}.body`),
          'KO'
        )
      })
    },

    resetFlowState() {
      this.setFlowState(null)
    },

    setViewContent(viewContent) {
      viewContent && viewContent.length > 0
        ? this.actionFlowSetViewContent(viewContent)
        : this.actionFlowSetViewContent([])
    },

    setViewOptions(options) {
      options
        ? this.actionFlowSetViewOptions(options)
        : this.actionFlowSetViewOptions({})
    },

    sendSentryEvent(errorMessage = errorMessage.message, callerName = null, extraData = null) {

      const sinisterId = this.getSinisterId || null;
      //component who is currently active
      const activeComponent = extraData ? extraData.activeComponent || this.$options.name : this.$options.name;
      //method who calls the Event
      callerName = callerName ? callerName.split(' ')[1] : `${activeComponent}Default`;
      
      Sentry.withScope(scope => {

        scope.setTag('sinisterId', sinisterId)
        scope.setTag('activeComponent', activeComponent)
        scope.setTag('callerName', callerName)

        if (extraData && extraData.hasUserInteracted !== undefined) 
          scope.setTag('hasUserInteracted', extraData.hasUserInteracted)

        const context = {
          sinisterId,
          activeComponent, 
          callerName, 
          errorMessage, 
          ...extraData
        }

        scope.setContext("data", context)
        
        //console.log(scope)
        Sentry.captureException(new Error(`[${activeComponent}.${callerName}] ${errorMessage}`))
      })
    },

    notifyAppOfChanges(component) {

      maEventBus.$emit('setActiveComponent', component)
      maEventBus.$emit('reinitializeUserInteraction')
    },

    isNumber(value) {
      return /^[0-9]+([,.][0-9]+)?$/.test(value)
    }

    // todo set mavalueHasImages
    // set flowResult
  },
  beforeCreate: function () {
    if (!(FLOW_MODULE_NAME in this.$store.state)) {
      this.$store.registerModule(FLOW_MODULE_NAME, flowModule)
    }
  }
}
